import axios from '@/utils/axios';
import {
  CreateTaskCommentDto,
  CreateTaskDto,
  GetTaskBoardsParams,
  MoveTaskDto,
  Task,
  TaskAttachment,
  TaskBoard,
  TaskBoardsResponse,
  TaskComment,
  TaskSection,
  UpdateTaskCommentDto,
  UpdateTaskDto,
} from '../types';

type ResponseEnvelope<T> = {
  data?: T;
  meta?: TaskBoardsResponse['meta'];
  message?: string;
};

export type TaskAttachmentAccessUrlResponse = {
  expires_in?: number;
  url: string;
};

const unwrapData = <T>(payload: unknown): T => {
  const response = payload as ResponseEnvelope<T>;

  if (response?.data !== undefined) {
    return response.data;
  }

  return payload as T;
};

const unwrapMessage = (payload: unknown): string | undefined =>
  (payload as ResponseEnvelope<unknown>)?.message;

const normalizeTaskBoards = (payload: unknown): TaskBoard[] => {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') return [payload as TaskBoard];

  return [];
};

const countTasksInBoards = (boards: TaskBoard[]) =>
  boards.reduce(
    (total, board) =>
      total +
      board.sections.reduce(
        (boardTotal, section) => boardTotal + (section.tasks ?? []).length,
        0
      ),
    0
  );

export async function getTaskBoards(
  params?: GetTaskBoardsParams
): Promise<TaskBoardsResponse> {
  const { data } = params
    ? await axios.get('/tasks/boards', { params })
    : await axios.get('/tasks/boards');
  const boards = normalizeTaskBoards(
    unwrapData<TaskBoardsResponse['data'] | TaskBoard>(data)
  );

  return {
    data: boards,
    meta: (data as ResponseEnvelope<TaskBoardsResponse['data']>).meta ?? {
      all: Boolean(params?.all),
      totalBoards: boards.length,
      totalTasks: countTasksInBoards(boards),
    },
    message: unwrapMessage(data),
  };
}

export async function getTaskSections(): Promise<TaskSection[]> {
  const { data } = await axios.get('/tasks/sections');
  const sections = unwrapData<TaskSection[]>(data);

  return Array.isArray(sections) ? sections : [];
}

export async function createTask(payload: CreateTaskDto): Promise<Task> {
  const { data } = await axios.post('/tasks', payload);

  return unwrapData<Task>(data);
}

export async function updateTask(
  id: number,
  payload: UpdateTaskDto
): Promise<Task> {
  const { data } = await axios.patch(`/tasks/${id}`, payload);

  return unwrapData<Task>(data);
}

export async function moveTask(
  id: number,
  payload: MoveTaskDto
): Promise<Task> {
  const { data } = await axios.patch(`/tasks/${id}/move`, payload);

  return unwrapData<Task>(data);
}

export async function addTaskComment(
  id: number,
  payload: CreateTaskCommentDto
): Promise<TaskComment> {
  const { data } = await axios.post(`/tasks/${id}/comments`, payload);

  return unwrapData<TaskComment>(data);
}

export async function updateTaskComment(
  id: number,
  commentId: number,
  payload: UpdateTaskCommentDto
): Promise<TaskComment> {
  const { data } = await axios.patch(
    `/tasks/${id}/comments/${commentId}`,
    payload
  );

  return unwrapData<TaskComment>(data);
}

export async function archiveTask(id: number): Promise<void> {
  await axios.delete(`/tasks/${id}`);
}

export async function uploadTaskAttachments(
  id: number,
  files: File[]
): Promise<Task | TaskAttachment[]> {
  const formData = new FormData();
  files.forEach((file) => formData.append('file', file));

  const { data } = await axios.post(`/tasks/${id}/attachments`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return unwrapData<Task | TaskAttachment[]>(data);
}

export async function getTaskAttachmentAccessUrl(
  id: number,
  attachmentId: string | number
): Promise<TaskAttachmentAccessUrlResponse> {
  const { data } = await axios.get(
    `/tasks/${id}/attachments/${attachmentId}/access-url`
  );

  return unwrapData<TaskAttachmentAccessUrlResponse>(data);
}

export async function deleteTaskAttachment(
  id: number,
  attachmentId: string | number
): Promise<void> {
  await axios.delete(`/tasks/${id}/attachments/${attachmentId}`);
}
